import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { KeyRound, Pencil, Trash2 } from 'lucide-react';
import { PERMISSIONS } from '@pms/shared';
import { getErrorMessage } from '@/lib/errors';
import { formatDate } from '@/lib/dateFormat';
import { usePermission } from '@/hooks/usePermission';
import { idManagementService, type UserAccountId } from '@/services/id-management.service';
import { usersService } from '@/services/users.service';
import { AccountUsersPopover } from '@/components/id-management/AccountUsersPopover';
import { CategoryLabel, CategoryOption } from '@/components/id-management/CategoryIcon';
import { IdCardList } from '@/components/id-management/IdCardList';
import { SortHeader } from '@/components/id-management/SortHeader';
import { EmployeeSelect } from '@/components/users/EmployeeSelect';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Notice } from '@/components/ui/notice';
import { SearchInput } from '@/components/ui/search-input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { IdAccountCard } from './components/IdAccountCard';
import { IdAccountFormDialog } from './components/IdAccountFormDialog';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 350;
const DEFAULT_SORT = 'created_at:desc';
const ALL = '__all__';

/** Cards have no column headers to click, so they sort from this list instead. */
const CARD_SORT_OPTIONS = [
  { value: DEFAULT_SORT, label: 'Newest first' },
  { value: 'category:asc', label: 'Category A–Z' },
  { value: 'account_identifier:asc', label: 'Account ID A–Z' },
  { value: 'updated_at:desc', label: 'Recently updated' },
];

type FormState = { account?: UserAccountId } | null;

/** py-1.5 lines the text up with the 32px icon buttons, as every cell in a row is top-aligned. */
const CELL_TEXT_CLASS = 'block py-1.5';

export function IdManagementPage() {
  const queryClient = useQueryClient();
  const { has } = usePermission();
  const canManage = has(PERMISSIONS.ID_MANAGEMENT_MANAGE);

  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get('page')) || 1;
  const sort = searchParams.get('sort') ?? DEFAULT_SORT;
  const userId = searchParams.get('user_id') ?? '';
  const categoryId = searchParams.get('category_id') ?? '';
  const q = searchParams.get('q') ?? '';

  const updateParams = (changes: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(changes)) {
        if (value) next.set(key, value);
        else next.delete(key);
      }
      return next;
    }, { replace: true });
  };

  const [search, setSearch] = useState(q);
  useEffect(() => {
    if (search === q) return undefined;
    const timer = setTimeout(() => updateParams({ q: search || null, page: null }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // updateParams is rebuilt each render; only the typed text should restart the timer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, q]);

  const [form, setForm] = useState<FormState>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserAccountId | null>(null);

  const accountsQuery = useQuery({
    queryKey: ['id-management', 'accounts', { page, sort, userId, categoryId, q }],
    queryFn: () => idManagementService.listAccounts({
      page,
      limit: PAGE_SIZE,
      sort,
      q: q || undefined,
      user_id: userId || undefined,
      category_id: categoryId || undefined,
    }),
  });

  const categoriesQuery = useQuery({
    queryKey: ['id-management', 'categories'],
    queryFn: () => idManagementService.listCategories(),
  });

  const usersQuery = useQuery({
    queryKey: ['users', 'picker'],
    queryFn: () => usersService.picker(),
  });

  const userOptions = useMemo(
    () => (usersQuery.data ?? []).map((u) => ({
      value: u.id,
      label: [`${u.first_name} ${u.last_name}`.trim(), u.employee_code && `(${u.employee_code})`]
        .filter(Boolean)
        .join(' '),
    })),
    [usersQuery.data],
  );

  const categories = categoriesQuery.data ?? [];
  const hasNoCategories = categoriesQuery.isSuccess && categories.length === 0;

  const deleteMut = useMutation({
    mutationFn: (id: string) => idManagementService.deleteAccount(id),
    onSuccess: () => {
      toast.success('ID deleted');
      void queryClient.invalidateQueries({ queryKey: ['id-management', 'accounts'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const sortHeader = (field: string, label: string) => (
    <SortHeader field={field} label={label} sort={sort} onSort={(s) => updateParams({ sort: s, page: null })} />
  );

  const columns: Column<UserAccountId>[] = [
    {
      key: 'category',
      header: sortHeader('category', 'Category'),
      className: 'w-36 min-w-[130px] align-top',
      cell: (row) => <CategoryLabel name={row.category_name} iconPath={row.category_icon_path} />,
    },
    {
      key: 'account_identifier',
      header: sortHeader('account_identifier', 'Account ID'),
      className: 'min-w-[170px] align-top',
      // Unlike break-words, this also lets the column shrink, so one long ID can't push the table wide.
      cell: (row) => <span className={`${CELL_TEXT_CLASS} text-foreground [overflow-wrap:anywhere]`}>{row.account_identifier}</span>,
    },
    {
      key: 'users',
      header: 'Users',
      className: 'w-56 min-w-[200px] align-top',
      cell: (row) => <AccountUsersPopover users={row.users} accountLabel={row.account_identifier} />,
    },
    {
      key: 'updated_at',
      header: sortHeader('updated_at', 'Updated'),
      className: 'w-28 align-top',
      cell: (row) => <span className={`${CELL_TEXT_CLASS} whitespace-nowrap text-muted-foreground`}>{formatDate(row.updated_at)}</span>,
    },
    ...(canManage ? [{
      key: 'actions',
      header: 'Actions',
      className: 'w-24 text-right align-top',
      cell: (row: UserAccountId) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-8 sm:w-8" aria-label={`Edit ${row.account_identifier}`} onClick={() => setForm({ account: row })}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-8 sm:w-8" aria-label={`Delete ${row.account_identifier}`} onClick={() => setDeleteTarget(row)}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    }] : []),
  ];

  const pagination = accountsQuery.data
    ? {
        page: accountsQuery.data.pagination.page,
        totalPages: accountsQuery.data.pagination.totalPages,
        total: accountsQuery.data.pagination.total,
        onPageChange: (p: number) => updateParams({ page: String(p) }),
      }
    : undefined;

  const hasFilters = Boolean(q || userId || categoryId);
  const emptyMessage = hasFilters ? 'No IDs match these filters.' : 'No IDs saved yet. Use Add ID to save the first one.';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-primary" />
          <h1 className="text-2xl font-semibold text-foreground">ID Management</h1>
        </div>
        {canManage && (
          <Button disabled={!categoriesQuery.isSuccess || !usersQuery.isSuccess || hasNoCategories} onClick={() => setForm({})}>
            + Add ID
          </Button>
        )}
      </div>

      {hasNoCategories && (
        <Notice
          tone="warning"
          compact
          icon={KeyRound}
          action={has(PERMISSIONS.ID_CATEGORIES_MANAGE) ? (
            <Button asChild size="sm" variant="outline">
              <Link to="/assets/master?tab=id-categories">Add categories</Link>
            </Button>
          ) : undefined}
        >
          No ID categories exist yet. A category such as Google or Microsoft is needed before an ID can be saved.
        </Notice>
      )}

      {(categoriesQuery.isError || usersQuery.isError) && (
        <Notice
          tone="danger"
          compact
          action={(
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                void categoriesQuery.refetch();
                void usersQuery.refetch();
              }}
            >
              Retry
            </Button>
          )}
        >
          The user and category lists could not be loaded, so filters and Add ID are unavailable.
        </Notice>
      )}

      {/* Two filters to a row on phones, so they don't push the first ID off the screen. */}
      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search user, category or account ID…"
          containerClassName="col-span-2 w-full sm:w-72"
        />
        <div className="w-full sm:w-60">
          <EmployeeSelect
            value={userId}
            onChange={(v) => updateParams({ user_id: v || null, page: null })}
            options={[{ value: '', label: 'All users' }, ...userOptions]}
            placeholder="All users"
          />
        </div>
        <Select
          value={categoryId || ALL}
          onValueChange={(v) => updateParams({ category_id: v === ALL ? null : v, page: null })}
        >
          <SelectTrigger className="h-9 w-full sm:w-48">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c.id} value={c.id}><CategoryOption name={c.name} iconPath={c.icon_path} /></SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="col-span-2 w-full sm:w-48 xl:hidden">
          <Select value={sort} onValueChange={(v) => updateParams({ sort: v, page: null })}>
            <SelectTrigger className="h-9 w-full" aria-label="Sort">
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              {CARD_SORT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* The table only fits without sideways scrolling from 1280px, so anything smaller gets cards. */}
      <div className="hidden xl:block">
        <DataTable
          columns={columns}
          rows={accountsQuery.data?.data ?? []}
          rowKey={(row) => row.id}
          loading={accountsQuery.isLoading}
          error={accountsQuery.isError}
          onRetry={() => void accountsQuery.refetch()}
          empty={emptyMessage}
          pagination={pagination}
        />
      </div>
      <IdCardList
        className="xl:hidden"
        rows={accountsQuery.data?.data ?? []}
        rowKey={(row) => row.id}
        loading={accountsQuery.isLoading}
        error={accountsQuery.isError}
        onRetry={() => void accountsQuery.refetch()}
        empty={emptyMessage}
        pagination={pagination}
        renderCard={(row) => (
          <IdAccountCard
            account={row}
            onEdit={canManage ? () => setForm({ account: row }) : undefined}
            onDelete={canManage ? () => setDeleteTarget(row) : undefined}
          />
        )}
      />

      {form && (
        <IdAccountFormDialog
          account={form.account}
          categories={categories}
          people={usersQuery.data ?? []}
          onClose={() => setForm(null)}
        />
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete this ID?"
        description={deleteTarget ? `${deleteTarget.account_identifier} will be removed for ${deleteTarget.users.length} ${deleteTarget.users.length === 1 ? 'user' : 'users'}.` : undefined}
        confirmLabel="Delete"
        onConfirm={() => { if (deleteTarget) deleteMut.mutate(deleteTarget.id); }}
      />
    </div>
  );
}
